import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, SafeAreaView } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { SwipeListView } from 'react-native-swipe-list-view';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import { scale } from '../../utils/scale.utils';
import BaseText from '../BaseText';
import ExpenseCard from './ExpenseCard';
import {
  setCurrentExpenseReportCardModalItem,
  updateReport,
  deleteExpenseReport,
  setChargesInUseForExpense,
} from '../../store/actions/expense';
import { openAlert } from '../../store/actions/alert';
import { useNavigation } from '@react-navigation/native';
import { i18nNS, useComponentTKey } from '../../i18n';
import { useTranslation } from 'react-i18next';
import { RowBack } from './ExpenseReportCard';
import BottomButton from '../BottomButton';
import { rightExpenseReportCardModalOption } from '../../utils/defaultNavOptions';
import { isNullOrEmpty } from '../../utils/common.utils';
import Icon from '../Icon';
import RowView from '../RowView';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency.utils';
import { selectCompanyCurrencyCulture } from '../../store/selectors/auth.selector';
import { chargeActions, expenseActions, REPORT_STATUS } from '../../constants/Enums';
import InputDialog from '../InputDialog';
import { ScrollView } from 'react-native-gesture-handler';
import { Screens, Stacks } from '../../constants/ScreenNames';
import { getRangeDate, hasIncompleteMandatoryFields } from '../../utils/expense.utils';
import { sendExpenseEmail } from '../../services/api/expense.service';
import { selectCurrentExpenseReportCardModalItem } from '../../store/selectors/expense.selector';
import { TextInput, Text } from 'react-native';

const { PENDING } = REPORT_STATUS;

export default function ExpenseReportCardModal({ route }) {
  const { item, onMoveItem, onAddExpenseItem, onsubmitExpense, onEditName } = route.params || {};
  const { t } = useTranslation(i18nNS.tripExpense);
  const { tKey } = useComponentTKey('ExpenseScreen');
  const navigation = useNavigation();
  const submitted = item.Status === 'Submitted';
  const dispatch = useDispatch();
  const currency =
    (item?.Expenses && item.Expenses.length > 0 && item.Expenses[0]?.Currency) || 'USD';
  const currencyCulture = useSelector(selectCompanyCurrencyCulture);
  const [editReportName, setEditReportName] = useState(false);
  const [reportRecipient, setReportRecipient] = useState(item.Recipient);
  const [editReportRecipient, setEditReportRecipient] = useState(false);
  const [reportMessage, setReportMessage] = useState(item.Message);
  const [editReportMessage, setEditReportMessage] = useState(false);
  const [expenseItem, setExpenseItem] = useState(item);
  const [errors, setErrors] = useState({});
  const [showExclamationIcon, setShowExclamationIcon] = useState(false);
  const currentExpenseReport = useSelector(selectCurrentExpenseReportCardModalItem);
  const user = useSelector((state) => state && state.auth && state.auth.user);
  const isEditable = item.Status === PENDING;
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <>
          <BaseText style={[CommonStyles.h2, CommonStyles.displayFlexCenter, CommonStyles.row]}>{item?.ReportNumber}</BaseText>
          <BaseText>{getRangeDate(item, 'MMM DD', t(tKey('To')))}</BaseText>
        </>
      ),
      headerTitleAlign: 'center',
      ...(!submitted ? rightExpenseReportCardModalOption(() => onAddExpenseItem()) : {}),
    });
  }, [navigation]);

  // Because of the calendar modal (which navigates back to this screen) and causes item to go undefined setting item in the redux store is necessary
  useEffect(() => {
    if (!isNullOrEmpty(item)) {
      dispatch(setCurrentExpenseReportCardModalItem(item));
    }
    setShowExclamationIcon(validateExpenses());
  }, [dispatch, item]);
  const validateExpenses = () => {
    let hasErrors = false;
    const errors = {};

    item?.Expenses?.forEach((expense) => {
      errors[expense.CategoryId] = false;

      if (hasIncompleteMandatoryFields(expense)) {
        errors[expense.CategoryId] = true;
        hasErrors = true;
      }
    });
    setErrors(errors);

    return hasErrors;
  };
  const onDelete = (expense, categoryId) => {
    const params = {
      ActionCode: expenseActions.EXPENSE_DELETE,
      //Receiving 'categoryId' from the microservice, but it's intended to be 'ExpenseGUID'
      ExpenseId: categoryId,
    };
    dispatch(
      openAlert({
        title: t(tKey('ConfirmRemove')),
        message: expense.Merchant,
        confirmText: t('common:Continue'),
        cancelText: t('common:Cancel'),
        onConfirmPressed: () => {
          dispatch(deleteExpenseReport(params));
          if (!isNullOrEmpty(expense?.Charge)) {
            showChargeBackInList(expense.Charge);
          }
          const updatedExpenses = expenseItem?.Expenses?.filter(
            (exp) => exp.CategoryId !== categoryId
          );
          const updatedItem = {
            ...expenseItem,
            Expenses: updatedExpenses,
          };
          setExpenseItem(updatedItem);
        },
      })
    );
  };

  const showChargeBackInList = (charge) => {
    const creditCardList = [
      {
        FirstSixLastFourCardNumber: charge?.FirstSixLastFourCardNumber,
        TransactionId: charge?.TransactionId,
      },
    ];
    dispatch(setChargesInUseForExpense(creditCardList));
  };

  const onReopenExpense = (item) => {
    dispatch(
      openAlert({
        title: t(tKey('ReopenTitle')),
        message: t(tKey('ReopenMessage')),
        confirmText: t('common:Confirm'),
        cancelText: t('common:Cancel'),
        onConfirmPressed: () => {
          dispatch(
            updateReport(expenseActions.UPDATE_STATUS_REPORT, item.ReportId, 'Status', PENDING)
          );
          const updatedItem = { ...item, Status: PENDING };
          navigation.navigate(Stacks.ExpenseStack, {
            screen: Screens.Expense,
            params: { resultingReport: updatedItem },
          });
        },
      })
    );
  };
  const [emailError, setEmailError] = useState('');
  const recipientInputRef = useRef(null);
  // Email validation function
    const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
    };
  return (
    <SafeAreaView style={styles.safeAreaView}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.card}>
          <RowView style={CommonStyles.justifyBetween}>
            <InputDialog
              label={t(tKey('ChangeExpenseName'))}
              defaultValue={item?.Name}
              visible={editReportName}
              onCancel={() => setEditReportName(false)}
              onSubmit={(text) => {
                const updatedItem = {
                  ...item,
                  Name: text,
                };

                dispatch(
                  updateReport(
                    expenseActions.UPDATE_EXPENSE_REPORT_NAME,
                    item.ReportId,
                    'Name',
                    text
                  )
                );

                dispatch(setCurrentExpenseReportCardModalItem(updatedItem));
                setEditReportName(false);
              }}
              loading={false}
            />
            <InputDialog
              label={t(tKey('ChangeReceipt'))}
              defaultValue={reportRecipient}
              visible={editReportRecipient}
              onCancel={() => setEditReportRecipient(false)}
              isEmailInput={true}
              onSubmit={(text) => {
                setReportRecipient(text);
                dispatch(
                  updateReport(expenseActions.UPDATE_REPORT, item.ReportId, 'Report', {
                    Recipient: text,
                  })
                );
                setEditReportRecipient(false);
              }}
              loading={false}
            />
            <InputDialog
              label={t(tKey('ChangeReceiptMessage'))}
              defaultValue={reportMessage}
              visible={editReportMessage}
              onCancel={() => setEditReportMessage(false)}
              onSubmit={(text) => {
                setReportMessage(text);
                dispatch(
                  updateReport(expenseActions.UPDATE_REPORT, item.ReportId, 'Report', {
                    Message: text,
                  })
                );
                setEditReportMessage(false);
              }}
              loading={false}
            />
            <RowView>
              <BaseText style={styles.title}>
                {item?.Name.length > 30 ? `${item?.Name.substring(0, 30)}...` : item?.Name}
              </BaseText>
              {onEditName && isEditable ? (
                <TouchableOpacity onPress={() => setEditReportName(true)}>
                  <Icon name="edit" style={styles.iconStyle} size={18} color={Color.primary} />
                </TouchableOpacity>
              ) : null}
              <BaseText style={styles.title}>
                {' - '}
                {item?.ReportNumber}
              </BaseText>
            </RowView>
            <BaseText style={styles.priceText}>
              {formatCurrency(item.ReportCost, currency, currencyCulture, 2)}
            </BaseText>
          </RowView>
          <SwipeListView
            data={expenseItem?.Expenses ? expenseItem.Expenses : []}
            keyExtractor={(ex) => ex.ExpenseId?.toString()}
            renderItem={(val) => (
              <View>
                <ExpenseCard
                  item={val.item}
                  isEditable={isEditable}
                  isReportCardModal={true}
                  showExclamationIcon={errors[val.item.CategoryId]}
                />
              </View>
            )}
            renderHiddenItem={(val, rowMap) => (
              <RowBack
                onMove={() => {
                  if (rowMap[val.item.ExpenseId]) {
                    rowMap[val.item.ExpenseId].closeRow();
                  }
                  onMoveItem(item, val.item.ExpenseId);
                }}
                onDelete={() => {
                  if (rowMap[val.item.ExpenseId]) {
                    rowMap[val.item.ExpenseId].closeRow();
                  }
                  onDelete(val.item, val.item.CategoryId);
                }}
              />
            )}
            rightOpenValue={isEditable ? scale(-112) : 0} // Disable swipe by setting open value to 0
            disableRightSwipe={!isEditable} // Disable right swipe when not editable
            disableLeftSwipe={!isEditable} // Disable left swipe when not editable
            previewRowKey={'0'}
            previewOpenValue={-40}
            previewOpenDelay={2000}
          />
          <View style={[styles.receipt, reportRecipient ? null : styles.emptyReceipt]}>
            <BaseText style={styles.receiptLabel}>{t(tKey('RecipientLabel'))}</BaseText>
            {!submitted ? (
                <>
                <TextInput
                    ref={recipientInputRef}
                    value={reportRecipient}
                    onChangeText={(text) => {
                    setReportRecipient(text);

                    if (!validateEmail(text)) {
                        setEmailError(t(tKey('invalidEmailFormat')));
                    } else {
                        setEmailError('');                        
                    }
                    }}
                    placeholder={t(tKey('recipientEmail'))}
                    style={[
                    CommonStyles.h2,
                    styles.textInput,
                    emailError ? styles.inputError : null,
                    ]}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    returnKeyType="done"
                    autoComplete="email"
                    onBlur={() => {
                    if (!validateEmail(reportRecipient)) {
                        setEmailError(t(tKey('invalidEmailFormat')) );
                    } else {
                        setEmailError('');
                    }
                    }}
                />
                {!!emailError && (
                    <Text style={styles.errorText}>{emailError}</Text>
                )}
                </>
            ) : (
                <BaseText style={[CommonStyles.h2, styles.textDisabled]}>
                {reportRecipient}
                </BaseText>
            )}
            </View>
          <View style={[styles.message, reportMessage ? null : styles.emptyReceipt]}>
            <BaseText style={styles.receiptLabel}>{t(tKey('MessageLabel'))}</BaseText>

            {!submitted ? (
                <TextInput
                value={reportMessage}
                onChangeText={(text) => {
                    setReportMessage(text);
                }}
                placeholder={t(tKey('message'))}
                style={[CommonStyles.h4, styles.textArea]}
                multiline={true}
                numberOfLines={4}
                textAlignVertical="top"
                returnKeyType="default"
                />
            ) : (
                <BaseText style={[CommonStyles.h4, styles.textDisabled]}>
                {reportMessage}
                </BaseText>
            )}
            </View>
        </View>

        <View>
          {item.Status === 'Pending' ? (
            <BottomButton
              label={'Submit Report'}
              disabled={showExclamationIcon}
              style={showExclamationIcon ? styles.disabledButton : styles.roundedBtn}
              onPress={() => {
                if (expenseItem.Expenses.length === 0) {
                  dispatch(
                    openAlert({
                      message: t(tKey('atLeastOneExpense')),
                    })
                  );
                  return;
                }
                if (!reportRecipient || reportRecipient.trim() === '') {
                    dispatch(
                    openAlert({ message: t(tKey('MissingEmail')) })
                    );
                    return;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(reportRecipient)) {
                    dispatch(
                    openAlert({ message: t(tKey('invalidEmail')) })
                    );
                    return;
                }                
                if (!reportMessage || reportMessage.trim() === '') {
                    dispatch(
                    openAlert({
                        message: t(tKey('emptyMessage')) })
                    );
                    return;
                }
                if (reportRecipient) {
                  const currencyCode = getCurrencySymbol(currency);
                  item.UserInfo = user.FirstName + ' ' + user.LastName;
                  onsubmitExpense({...currentExpenseReport, reportRecipient, reportMessage});                  sendExpenseEmail(
                    item.ReportId,
                    reportRecipient,
                    item.Name,
                    item.Expenses,
                    item.Status,
                    item.StartDate,
                    item.EndDate,
                    formatCurrency(item.ReportCost, currency, currencyCulture, 2),
                    currencyCode?.symbol || '$',
                    item.Message,
                    item
                  );
                } else {
                  dispatch(
                    openAlert({ message: t(tKey('MissingEmail')) })
                  );
                }
              }}
            />
          ) : (
            <BottomButton label={t('common:ReOpen')} onPress={() => onReopenExpense(item)} />
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: Color.accent,
    shadowColor: 'rgba(0, 0, 0, 0.2)',
    shadowOpacity: 1,
    shadowRadius: 3.84,
    elevation: 5,
    ...CommonProperties.mx3,
    marginBottom: scale(100),
    ...CommonProperties.borderRadius2,
  },
  roundedBtn: {
    backgroundColor: Color.primary,
    ...CommonProperties.borderRadius4,
  },
  safeAreaView: { flex: 1, backgroundColor: Color.accent },
  title: {
    ...CommonProperties.h4,
    ...CommonProperties.mr3,
    ...CommonProperties.mx3,
    ...CommonProperties.my4,
    fontSize: scale(12),
  },
  priceText: {
    ...CommonStyles.h2,
    marginRight: 10,
    marginTop: 10,
    fontSize: scale(16),
  },
  iconStyle: { marginTop: 14 },
  scrollView: {
    flex: 1,
  },
  receipt: {
    borderBottomColor: Color.disabled,
    borderBottomWidth: 1,
    ...CommonProperties.p5,
  },
  message: {
    ...CommonProperties.p5,
    paddingBottom: '3rem',
  },
  emptyReceipt: {
    ...CommonProperties.p5,
  },
  receiptLabel: {
    ...CommonProperties.h4,
    ...CommonProperties.mb2,
    color: Color.text.medium,
  },
  textDisabled: {
    color: Color.text.medium,
  },
  disabledButton: {
    ...CommonProperties.borderRadius4,
    backgroundColor: Color.grayText2,
  },
  textInput: {
  borderBottomWidth: 0,
  borderBottomColor: Color.disabled,
  paddingVertical: 6,
  color: Color.text.dark,
},
inputError: {
  borderBottomColor: 'red',
},
errorText: {
  color: 'red',
  fontSize: 12,
  marginTop: 4,
},
textArea: {
  borderWidth: 0,
  borderColor: Color.disabled,
  borderRadius: 6,
  padding: 10,
  minHeight: 100,
  color: Color.text.dark,
},
});
